"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.entityUnusedRule = void 0;
exports.isEntityUnused = isEntityUnused;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const database_1 = require("../../database");
const databaseUtils_1 = require("../../databaseUtils");
const date_1 = require("../../helpers/date");
const duration_1 = require("../../helpers/duration");
const rule_1 = require("../rule");
const ruleId = 'entity-unused';
const ruleName = 'unused entity';
const ruleDescription = 'entities with the same number of scans (sequential & indexes) during the threshold period or the last scan older than the threshold';
const CustomRuleConf = rule_1.RuleConf.extend({
    ignores: database_1.EntityId.array().optional(),
    minDays: zod_1.z.number()
}).strict().describe('EntityUnusedConf');
exports.entityUnusedRule = {
    id: ruleId,
    name: ruleName,
    description: ruleDescription,
    conf: { level: rule_1.RuleLevel.enum.medium, minDays: 10 },
    zConf: CustomRuleConf,
    analyze(conf, now, db, queries, history, reference) {
        const refIgnores = reference.map(r => r.entity).filter(utils_1.isNotUndefined);
        const ignores = refIgnores.concat(conf.ignores?.map(databaseUtils_1.entityRefFromId) || []);
        const historyEntitiesById = history.map(h => ({ report: h.report, date: h.date, entities: (0, utils_1.indexBy)(h.database.entities || [], databaseUtils_1.entityToId) }));
        return (db.entities || [])
            .filter(e => !ignores.some(i => (0, databaseUtils_1.entityRefSame)(i, (0, databaseUtils_1.entityToRef)(e))))
            .map(e => {
            const previous = historyEntitiesById.map(h => {
                const entity = h.entities[(0, databaseUtils_1.entityToId)(e)];
                return entity ? { report: h.report, date: h.date, entity } : undefined;
            }).filter(utils_1.isNotUndefined);
            return isEntityUnused(now, e, previous, conf.minDays);
        })
            .filter(utils_1.isNotUndefined)
            .map(r => ({
            ruleId,
            ruleName,
            ruleLevel: conf.level,
            message: `Entity ${(0, databaseUtils_1.entityToId)(r.current)} is unused since ${(0, date_1.showDate)(r.date)} (check all instances to be sure!).`,
            entity: (0, databaseUtils_1.entityToRef)(r.current),
            extra: (0, utils_1.removeUndefined)({
                previousReport: r.report,
                previousDate: new Date(r.date).toISOString(),
                previous: r.previous?.stats,
                current: r.current.stats,
            })
        }));
    }
};
function isEntityUnused(now, current, history, minDays) {
    const { scanSeq, scanIdx, scanSeqLast, scanIdxLast } = current.stats || {};
    const scans = (scanSeq || 0) + (scanIdx || 0);
    if (scans > 0) {
        const sameAsCurrent = history.filter(h => now - h.date > minDays * duration_1.oneDay).map(h => {
            const hScans = (h.entity.stats?.scanSeq || 0) + (h.entity.stats?.scanIdx || 0);
            return hScans === scans ? { date: h.date, current, previous: h.entity, report: h.report } : undefined;
        }).filter(utils_1.isNotUndefined);
        const res = (0, utils_1.minBy)(sameAsCurrent, d => d.date);
        if (res)
            return res;
    }
    if (scanSeqLast || scanIdxLast) {
        const lastSeqScan = scanSeqLast ? new Date(scanSeqLast).getTime() : 0;
        const lastIdxScan = scanIdxLast ? new Date(scanIdxLast).getTime() : 0;
        const lastScan = Math.max(lastSeqScan, lastIdxScan);
        if (now - lastScan > minDays * duration_1.oneDay) {
            return { date: lastScan, current };
        }
    }
    return undefined;
}
//# sourceMappingURL=entityUnused.js.map